<script setup lang="ts">
import { computed } from 'vue';
import { NxtRadioButton, NxtIconButton, NxtDropdown } from '@future-harry/nxt-styles';
import { useLanguagesStore } from '@/core/collections/languages/languages.store';
import type { Language } from '@future-harry/nxt-vue-library';
import { useI18n } from 'vue-i18n';
import { useSettingsStore } from '@/apps/admin/stores/settings.store';

const { locale } = useI18n();
const defaultLanguageId = defineModel<number>('defaultLanguageId', { required: true });
const languageIds = defineModel<number[]>('languageIds', { required: true });
const languages = defineModel<Language[]>('languages', { required: true });
const settingsStore = useSettingsStore();
const languagesStore = useLanguagesStore();

const jobBoardSettingsErrors = computed(() => settingsStore.validationErrors.value);
const allLanguagesOptionsFormat = computed(() => languagesStore.allLanguages.map((l) => ({ id: l.id, name: l.name_translation })));

function addLanguage(id: number) {
   // Prevent adding duplicates
   if (languageIds.value.some((l) => l === id)) return;

   const updatedAdded = [...languageIds.value, id];
   languageIds.value = updatedAdded;

   // If this is the first language, set as default
   if (updatedAdded.length === 1) {
      defaultLanguageId.value = id;
   }
}

function removeLanguage(id: number) {
   // Prevent removing last language
   if (languageIds.value.length === 1) return;

   const updatedRemoved = languageIds.value.filter((l) => l !== id);
   languageIds.value = updatedRemoved;

   // If the removed language was the selected default, select another
   if (defaultLanguageId.value === id) {
      defaultLanguageId.value = updatedRemoved[0];
   }
}

function getLanguageNames(id: number) {
   const lang = languages.value.find((l) => l.id === id);

   if (lang) {
      return lang.name_translation[locale.value];
   } else if (languagesStore.allLanguages && languagesStore.allLanguages.find((l) => l.id === id)) {
      return languagesStore.allLanguages.find((l) => l.id === id)?.name_translation;
   }

   return '';
}
</script>

<template>
   <div class="language-selector">
      <div class="language-header-row">
         <span class="language-header-name"></span>
         <span class="language-header-radio">
            <span v-if="languageIds.length > 0">{{ $t('job-board.settings.language_set_default') }}</span>
         </span>
         <span class="language-header-trash"></span>
      </div>
      <div v-for="id in languageIds" :key="id" class="language-row">
         <span class="language-name">{{ getLanguageNames(id) }}</span>
         <NxtRadioButton v-model="defaultLanguageId" :name="`default-language-${id}`" :value="id" class="language-radio" />
         <NxtIconButton name="trash" :disabled="languageIds.length === 1" @click="removeLanguage(id)" class="language-trash" />
      </div>
      <NxtDropdown
         :name="`language_ids`"
         :errors="jobBoardSettingsErrors"
         :options="allLanguagesOptionsFormat"
         @update:model-value="addLanguage"
         :placeholder="$t('components.language-selector.add-placeholder')"
         class="language-dropdown"
      />
   </div>
</template>

<style lang="scss" scoped>
.language-selector {
   display: flex;
   flex-direction: column;
   gap: 1rem;
}
.language-header-row {
   display: flex;
   align-items: center;
   justify-content: flex-start;
   gap: 1.5rem;
   padding-bottom: 0.25rem;
}
.language-header-name {
   flex: 1;
}
.language-header-radio {
   min-width: 120px;
   display: flex;
   justify-content: center;
   align-items: center;
   text-align: center;
   font-size: 0.95rem;
   font-weight: 500;
}
.language-header-trash {
   width: 32px;
}
.language-row {
   display: flex;
   align-items: center;
   justify-content: flex-start;
   gap: 1.5rem;
}
.language-name {
   font-size: 1rem;
   flex: 1;
}
.language-radio {
   min-width: 120px;
   display: flex;
   justify-content: center;
   align-items: center;
   margin-right: 0;
}
.language-trash {
   margin-left: auto;
}
</style>
